// src/screens/admin/VuelosGestores.jsx
// Sub-sección "Gestores" del panel Vuelos Guyana (Tema 4): crear gestores
// con cuenta propia, ver su crédito acumulado y registrar comisiones/pagos.
// El gestor mismo ve su propio link, comisión y crédito desde su cuenta
// (VuelosGestorPropio.jsx) -- acá es solo la vista del admin.
import React, { useState, useEffect } from 'react';
import { toast } from '../../components/UI';
import {
  getGestores, crearGestor, actualizarGestor, registrarComisionGestor, registrarPagoGestor, buscarPerfilPorEmail,
  getVuelosEventos, cerrarLeadGestor, revertirCierreLeadGestor, pagarLeadGestor, marcarLeadEscribio, setGestorEsPrueba, eliminarGestor,
} from '../../services/vuelosGuyanaService';

const LINK_BASE = 'https://app.clicktravelagency.com.br/vuelos-guyana';

const formatFechaLead = (iso) =>
  new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });

// Con hora, para poder cruzar un toque con el chat de WhatsApp.
const formatFechaHoraLead = (iso) =>
  new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// Los botones generales (el de arriba y el del grupo de WhatsApp) no llevan ruta.
const etiquetaRuta = (l) => (l.ciudad_salida && l.destino ? `${l.ciudad_salida} → ${l.destino}` : 'Botón general o grupo');

// Dos listas separadas. "Toques en WhatsApp": alguien abrió WhatsApp desde el
// link del gestor -- no se sabe si llegó a escribir. "Solicitudes": Yoedis
// confirmó que esa persona sí le escribió; de ahí se cierra y se paga la venta.
function LeadsGestor({ leads, onChanged }) {
  const [montoDraft, setMontoDraft] = useState({});
  const [savingId, setSavingId] = useState(null);

  if (leads.length === 0) return null;

  const toques = leads.filter((l) => l.lead_estado === 'toque');
  const solicitudes = leads.filter((l) => l.lead_estado !== 'toque');

  const handleEscribio = async (leadId, escribio) => {
    setSavingId(leadId);
    try {
      await marcarLeadEscribio(leadId, escribio);
      onChanged();
    } catch { toast('Error al actualizar.', 'error'); }
    setSavingId(null);
  };

  const handleCerrar = async (leadId) => {
    const monto = montoDraft[leadId];
    if (!monto || parseFloat(monto) <= 0) return toast('Pon el monto de la venta.', 'error');
    setSavingId(leadId);
    try {
      await cerrarLeadGestor(leadId, monto);
      toast('Venta cerrada ✅');
      onChanged();
    } catch { toast('Error al cerrar la venta.', 'error'); }
    setSavingId(null);
  };

  const handleRevertir = async (leadId) => {
    if (!window.confirm('¿Deshacer este cierre? Se borra la comisión que se había registrado.')) return;
    setSavingId(leadId);
    try {
      await revertirCierreLeadGestor(leadId);
      toast('Cierre deshecho ✅');
      onChanged();
    } catch { toast('Error al deshacer.', 'error'); }
    setSavingId(null);
  };

  const handlePagar = async (leadId) => {
    if (!window.confirm('¿Confirmas que ya le transferiste el dinero de esta venta?')) return;
    setSavingId(leadId);
    try {
      await pagarLeadGestor(leadId);
      toast('Pago confirmado ✅');
      onChanged();
    } catch { toast('Error al confirmar el pago.', 'error'); }
    setSavingId(null);
  };

  return (
    <>
    {toques.length > 0 && (
      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>Toques en WhatsApp ({toques.length})</summary>
        <p style={{ fontSize: 10, color: 'var(--muted)', margin: '6px 0', lineHeight: 1.4 }}>
          Alguien abrió WhatsApp desde este link. Si te escribió, márcalo y pasa a Solicitudes.
        </p>
        {toques.map((l) => (
          <div key={l.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--navy-border)' }}>
            <span style={{ fontSize: 11, color: 'var(--off-white)', flex: '1 0 140px' }}>
              {formatFechaHoraLead(l.created_at)} · {etiquetaRuta(l)}
            </span>
            <button onClick={() => handleEscribio(l.id, true)} disabled={savingId === l.id} style={{
              background: 'rgba(0,196,140,0.12)', border: '1px solid var(--success)', color: 'var(--success)',
              borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
            }}>✅ Me escribió</button>
          </div>
        ))}
      </details>
    )}
    {solicitudes.length > 0 && (
    <details style={{ marginTop: 12 }}>
      <summary style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>Solicitudes ({solicitudes.length})</summary>
      {solicitudes.map((l) => (
        <div key={l.id} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--navy-border)' }}>
          <span style={{ fontSize: 11, color: 'var(--off-white)', flex: '1 0 140px' }}>
            {formatFechaLead(l.created_at)} · {etiquetaRuta(l)}
          </span>
          {l.lead_estado === 'pendiente' && (
            <>
              <button onClick={() => handleEscribio(l.id, false)} disabled={savingId === l.id} style={{
                background: 'none', border: 'none', color: 'var(--muted)', fontSize: 10, textDecoration: 'underline',
              }}>no escribió</button>
              <input
                type="number" inputMode="decimal" placeholder="USD" style={{ width: 70, fontSize: 12 }}
                value={montoDraft[l.id] ?? ''} onChange={(e) => setMontoDraft((d) => ({ ...d, [l.id]: e.target.value }))}
              />
              <button onClick={() => handleCerrar(l.id)} disabled={savingId === l.id} style={{
                background: 'rgba(0,196,140,0.12)', border: '1px solid var(--success)', color: 'var(--success)',
                borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              }}>Cerrar venta</button>
            </>
          )}
          {l.lead_estado === 'cerrado' && (
            <>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)' }}>Cerrada · le debes ${parseFloat(l.lead_monto_usd).toFixed(0)}</span>
              <button onClick={() => handlePagar(l.id)} disabled={savingId === l.id} style={{
                background: 'rgba(0,196,140,0.12)', border: '1px solid var(--success)', color: 'var(--success)',
                borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              }}>✅ Confirmar pago</button>
              <button onClick={() => handleRevertir(l.id)} disabled={savingId === l.id} style={{
                background: 'none', border: 'none', color: 'var(--muted)', fontSize: 10, textDecoration: 'underline',
              }}>deshacer</button>
            </>
          )}
          {l.lead_estado === 'pagado' && (
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)' }}>✅ Pagada · ${parseFloat(l.lead_monto_usd).toFixed(0)}</span>
          )}
        </div>
      ))}
    </details>
    )}
    </>
  );
}

function NuevoGestor({ onCreado }) {
  const [email, setEmail] = useState('');
  const [perfil, setPerfil] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [comision, setComision] = useState('20');
  const [creando, setCreando] = useState(false);

  const handleBuscar = async () => {
    if (!email.trim()) return;
    setBuscando(true);
    const p = await buscarPerfilPorEmail(email);
    setBuscando(false);
    if (!p) return toast('No hay ningún registrado con ese correo.', 'error');
    setPerfil(p);
    setNombre(p.name || '');
  };

  const handleCrear = async () => {
    if (!perfil) return toast('Busca primero el correo del gestor.', 'error');
    if (!codigo.trim()) return toast('Falta el código del link.', 'error');
    if (!nombre.trim()) return toast('Falta el nombre.', 'error');
    setCreando(true);
    try {
      await crearGestor(perfil.id, codigo, nombre, comision);
      toast(`${nombre} ya es gestor ✅`);
      setEmail(''); setPerfil(null); setCodigo(''); setNombre(''); setComision('20');
      onCreado();
    } catch (err) {
      toast(err.message?.includes('duplicate') ? 'Ese código ya está en uso.' : 'Error al crear el gestor.', 'error');
    }
    setCreando(false);
  };

  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
      <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)', marginBottom: 10 }}>Nuevo gestor</p>

      <label>Correo con el que ya se registró en la app</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@ejemplo.com" style={{ flex: 1 }} />
        <button onClick={handleBuscar} disabled={buscando} style={{ background: 'var(--navy)', border: '1px solid var(--navy-border)', borderRadius: 8, padding: '0 14px', color: 'var(--white)', fontSize: 12, fontWeight: 700 }}>
          {buscando ? '...' : 'Buscar'}
        </button>
      </div>

      {perfil && (
        <>
          <p style={{ fontSize: 12, color: 'var(--success)', marginBottom: 10 }}>✅ Encontrado: {perfil.name || perfil.email}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div><label>Nombre a mostrar</label><input value={nombre} onChange={(e) => setNombre(e.target.value)} /></div>
            <div><label>Código del link</label><input value={codigo} onChange={(e) => setCodigo(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))} placeholder="ej. pedro" /></div>
          </div>
          <label>Comisión máxima por venta (USD)</label>
          <input type="number" inputMode="decimal" value={comision} onChange={(e) => setComision(e.target.value)} style={{ marginBottom: 12 }} />
          <button className="btn-primary" onClick={handleCrear} disabled={creando}>
            {creando ? <span className="spinner" /> : 'Crear gestor'}
          </button>
        </>
      )}
    </div>
  );
}

function MovimientoForm({ tipo, onSubmit }) {
  const [monto, setMonto] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const label = tipo === 'comision' ? 'Registrar venta cerrada' : 'Registrar pago';
  const color = tipo === 'comision' ? 'var(--success)' : 'var(--orange)';

  const handleSubmit = async () => {
    if (!monto || parseFloat(monto) <= 0) return toast('Monto inválido.', 'error');
    setEnviando(true);
    try {
      await onSubmit(monto, nota);
      setMonto(''); setNota('');
    } catch { toast('Error al guardar.', 'error'); }
    setEnviando(false);
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
      <input type="number" inputMode="decimal" value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="USD" style={{ width: 70, fontSize: 12 }} />
      <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (opcional)" style={{ flex: 1, fontSize: 12 }} />
      <button onClick={handleSubmit} disabled={enviando} style={{
        background: 'transparent', border: `1px solid ${color}`, color, borderRadius: 8, padding: '7px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
      }}>{enviando ? '...' : label}</button>
    </div>
  );
}

function GestorRow({ gestor, leads, onChanged }) {
  const [comision, setComision] = useState(gestor.comision_max_usd);
  const [activo, setActivo] = useState(gestor.activo);
  const [guardando, setGuardando] = useState(false);
  const [savingPrueba, setSavingPrueba] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const link = `${LINK_BASE}?g=${gestor.codigo}`;

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      await actualizarGestor(gestor.id, comision, activo);
      toast('Actualizado ✅');
      onChanged();
    } catch { toast('Error al guardar.', 'error'); }
    setGuardando(false);
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast('Link copiado 📋');
    } catch {
      toast('No se pudo copiar automáticamente -- mantén presionado el link para copiarlo.', 'error');
    }
  };

  const handleToggleEsPrueba = async () => {
    setSavingPrueba(true);
    try {
      await setGestorEsPrueba(gestor.id, !gestor.es_prueba);
      onChanged();
    } catch { toast('Error al actualizar.', 'error'); }
    setSavingPrueba(false);
  };

  const handleEliminar = async () => {
    const aviso = gestor.credito > 0
      ? `Todavía le debes $${gestor.credito.toFixed(0)}. Si lo eliminas, se borra también ese historial de comisiones. `
      : '';
    if (!window.confirm(`${aviso}¿Eliminar a ${gestor.nombre}? Su link (${link}) deja de funcionar. Esto no se puede deshacer.`)) return;
    setEliminando(true);
    try {
      await eliminarGestor(gestor.id);
      toast('Gestor eliminado ✅');
      onChanged();
    } catch { toast('Error al eliminar.', 'error'); setEliminando(false); }
  };

  return (
    <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--white)' }}>{gestor.nombre}{gestor.es_prueba ? ' 🧪' : ''}</p>
          <p style={{ fontSize: 11, color: 'var(--muted)' }}>{gestor.profiles?.email}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 10, color: 'var(--muted)' }}>LE DEBES</p>
          <p style={{ fontSize: 16, fontWeight: 800, color: gestor.credito > 0 ? 'var(--orange)' : 'var(--muted)' }}>${gestor.credito.toFixed(0)}</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <a href={link} target="_blank" rel="noopener noreferrer" style={{
          flex: 1, display: 'block', background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.25)',
          borderRadius: 8, padding: '8px 10px', fontSize: 11, color: 'var(--off-white)', fontFamily: 'monospace', textDecoration: 'none', wordBreak: 'break-all',
        }}>🔗 {link}</a>
        <button onClick={copiarLink} title="Copiar link" style={{
          flexShrink: 0, background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.25)',
          borderRadius: 8, padding: '0 12px', fontSize: 14, color: 'var(--orange)',
        }}>📋</button>
      </div>

      <button onClick={handleToggleEsPrueba} disabled={savingPrueba} title="Excluye el tráfico de este gestor de las estadísticas generales -- úsalo para cuentas de prueba" style={{
        display: 'block', width: '100%', fontSize: 11, fontWeight: 700, borderRadius: 8, padding: '7px 0', marginBottom: 10,
        background: gestor.es_prueba ? 'rgba(255,184,0,0.12)' : 'var(--navy)',
        color: gestor.es_prueba ? 'var(--gold)' : 'var(--muted)',
        border: `1px solid ${gestor.es_prueba ? 'rgba(255,184,0,0.4)' : 'var(--navy-border)'}`,
      }}>{gestor.es_prueba ? '🧪 Cuenta de prueba -- no cuenta en estadísticas' : 'Marcar como cuenta de prueba'}</button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end', marginBottom: 10 }}>
        <div><label>Comisión máxima (USD)</label><input type="number" inputMode="decimal" value={comision} onChange={(e) => setComision(e.target.value)} /></div>
        <button onClick={() => setActivo((a) => !a)} style={{
          fontSize: 11, fontWeight: 700, borderRadius: 8, padding: '9px 10px', whiteSpace: 'nowrap',
          background: activo ? 'rgba(0,196,140,0.15)' : 'var(--navy)',
          color: activo ? 'var(--success)' : 'var(--muted)',
          border: `1px solid ${activo ? 'rgba(0,196,140,0.4)' : 'var(--navy-border)'}`,
        }}>{activo ? '✅ Activo' : 'Inactivo'}</button>
      </div>
      <button onClick={handleGuardar} disabled={guardando} style={{ width: '100%', background: 'var(--navy)', border: '1px solid var(--orange)', color: 'var(--orange)', borderRadius: 8, padding: '9px 0', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
        {guardando ? '...' : '💾 Guardar comisión / estado'}
      </button>

      <MovimientoForm tipo="comision" onSubmit={(monto, nota) => registrarComisionGestor(gestor.id, monto, nota).then(onChanged)} />
      <MovimientoForm tipo="pago" onSubmit={(monto, nota) => registrarPagoGestor(gestor.id, monto, nota).then(onChanged)} />

      <LeadsGestor leads={leads} onChanged={onChanged} />

      {gestor.movimientos.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>Ver historial ({gestor.movimientos.length})</summary>
          {gestor.movimientos.map((m) => (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted)', padding: '4px 0', borderBottom: '1px solid var(--navy-border)' }}>
              <span>{m.tipo === 'comision' ? '➕ Venta' : '➖ Pago'}{m.nota ? ` · ${m.nota}` : ''}</span>
              <span>${parseFloat(m.monto_usd).toFixed(0)}</span>
            </div>
          ))}
        </details>
      )}

      <button onClick={handleEliminar} disabled={eliminando} style={{
        display: 'block', width: '100%', marginTop: 14, background: 'rgba(255,68,85,0.08)', border: '1px solid rgba(255,68,85,0.35)',
        color: 'var(--error)', borderRadius: 8, padding: '9px 0', fontSize: 12, fontWeight: 700,
      }}>{eliminando ? '...' : '🗑️ Eliminar gestor'}</button>
    </div>
  );
}

export default function VuelosGestores() {
  const [gestores, setGestores] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    const [g, e] = await Promise.all([getGestores(), getVuelosEventos(365)]);
    setGestores(g);
    setEventos(e);
    setLoading(false);
  };
  useEffect(() => { cargar(); }, []);

  const leadsPorCodigo = {};
  eventos.filter((e) => e.evento === 'clic_whatsapp' && e.gestor_codigo).forEach((e) => {
    if (!leadsPorCodigo[e.gestor_codigo]) leadsPorCodigo[e.gestor_codigo] = [];
    leadsPorCodigo[e.gestor_codigo].push(e);
  });

  return (
    <div>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 16, lineHeight: 1.5 }}>
        Cada gestor comparte su propio link. El cliente siempre ve el precio completo -- solo cambia si el gestor se aplica un descuento de su propia comisión (lo hace desde su cuenta). En cada gestor, "Toques en WhatsApp" son las veces que alguien abrió WhatsApp desde su link (puede que no haya escrito). Cuando alguien te escribe de verdad, márcalo con "Me escribió" y pasa a "Solicitudes", donde cierras y pagas la venta. También puedes usar los campos de abajo para una comisión suelta sin solicitud asociada.
      </p>

      <NuevoGestor onCreado={cargar} />

      {loading ? (
        <p style={{ color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Cargando gestores...</p>
      ) : gestores.length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 20 }}>Todavía no hay gestores creados.</p>
      ) : (
        gestores.map((g) => <GestorRow key={g.id} gestor={g} leads={leadsPorCodigo[g.codigo] || []} onChanged={cargar} />)
      )}
    </div>
  );
}
